/**
 * Shared Sentry error filtering utilities for both server and client
 * These functions help reduce noise from third-party scripts and theme code
 */

/**
 * Checks if an error is from a third-party script or theme code that should be suppressed
 * @param {any} event - Sentry event for additional context
 * @returns {boolean} true if this is a third-party/theme error that should be suppressed
 */
export function isThirdPartyOrThemeError(event) {
  // Check stacktrace frames for third-party script paths
  const frames = event.exception?.values?.[0]?.stacktrace?.frames || []

  for (const frame of frames) {
    const filename = frame.filename || ''

    // Shopify privacy banner script
    if (filename.includes('/cdn/shopifycloud/privacy-banner/')) {
      return true
    }

    // Uploadcare scripts
    if (
      filename.includes('uploadcare-uploadkit') ||
      filename.includes('uploadcare')
    ) {
      return true
    }

    // Theme code (theme.js or theme assets)
    if (
      filename.includes('/cdn/shop/t/') ||
      filename.includes('/assets/theme.js')
    ) {
      return true
    }

    // Shopify CDN scripts (not our code - our CDN is cdn.akadenia.com)
    // Our code is never served from Shopify's CDN, so we suppress all errors from it
    if (filename.includes('/cdn/shopifycloud/')) {
      return true
    }
  }

  return false
}

/**
 * Checks if an error is a generic network/load error from third-party sources
 * @param {any} event - Sentry event for additional context
 * @returns {boolean} true if this is a generic third-party network error
 */
export function isGenericThirdPartyNetworkError(event) {
  const errorMessage = event.exception?.values?.[0]?.value || ''
  const errorType = event.exception?.values?.[0]?.type || ''

  // Generic load failures (includes "Load failed" or "Load failed (url)" from fetch)
  const isLoadFailed =
    (errorType === 'TypeError' && errorMessage.includes('Load failed')) ||
    errorMessage.includes('TypeError: Load failed')
  if (isLoadFailed) {
    // Only suppress if it's from a third-party script or has no meaningful stacktrace
    const frames = event.exception?.values?.[0]?.stacktrace?.frames || []
    const hasOurCode = frames.some(
      (frame) =>
        frame.filename?.includes('akadenia') ||
        frame.filename?.includes('app-embed') ||
        frame.filename?.includes('upload-button')
    )

    // Suppress if no frames from our code
    if (!hasOurCode || frames.length === 0) {
      return true
    }
  }

  // Generic script errors without meaningful context
  if (
    errorMessage === 'Script error.' ||
    errorMessage ===
      'ErrorEvent: Event `ErrorEvent` captured as exception with message `Script error.`'
  ) {
    // Suppress generic script errors (usually CORS-related from third-party scripts)
    return true
  }

  return false
}

/**
 * Checks if an error is a CustomElementRegistry error from third-party scripts
 * @param {any} event - Sentry event for additional context
 * @returns {boolean} true if this is a CustomElementRegistry error from third-party scripts
 */
export function isThirdPartyCustomElementError(event) {
  const errorMessage = event.exception?.values?.[0]?.value || ''
  const frames = event.exception?.values?.[0]?.stacktrace?.frames || []

  // Filter out CustomElementRegistry errors from third-party scripts (like Uploadcare)
  if (
    errorMessage.includes(
      "Failed to execute 'define' on 'CustomElementRegistry'"
    ) &&
    frames.some(
      (frame) =>
        frame.filename?.includes('uploadcare') ||
        frame.filename?.includes('/cdn/shopifycloud/')
    )
  ) {
    return true
  }

  return false
}

/**
 * Main filter function that combines all third-party error checks
 * Use this in Sentry's beforeSend hook
 * @param {any} event - Sentry event
 * @returns {any | null} null if error should be suppressed, otherwise returns the event
 */
export function filterThirdPartyErrors(event) {
  // Filter out third-party script and theme errors
  if (isThirdPartyOrThemeError(event)) {
    return null
  }

  // Filter out generic third-party network errors
  if (isGenericThirdPartyNetworkError(event)) {
    return null
  }

  // Filter out CustomElementRegistry errors from third-party scripts
  if (isThirdPartyCustomElementError(event)) {
    return null
  }

  return event
}
